import { useMemo } from 'react';
import { useGetAllInterviewerPools, type InterviewerPool } from '@/api';
import { SelectWithRightCompOption } from '@/components/formBuilder/SelectComponents';
import { InterviewPoolPopover } from './InterviewPoolPopover';
import { InterviewPoolList } from './InterviewPoolList';

type InterviewerPoolConfigProps = {
  config?: {
    name?: string;
    label?: string;
    options?: InterviewerPool[];
    [key: string]: any;
  };
};

export const useInterviewerPoolConfig = ({
  config,
}: InterviewerPoolConfigProps = {}) => {
  const { name = 'interview_pool_id', ...overRides } = config ?? {};

  const { interviewerPools, isLoading } = useGetAllInterviewerPools();

  const interviewPoolsOptions = useMemo(() => {
    return (interviewerPools ?? []).map((pool) => ({
      ...pool,
      value: pool.id,
      label: pool.name,
    }));
  }, [interviewerPools]);

  const formConfig = useMemo(() => {
    return {
      isConditional: true,
      conditionConfig: {
        type: 'and',
        conditions: [
          { field: 'interview_type', value: 'one_on_one' },
          { field: 'interviewTypeRadio', value: 'interview_pool' },
        ],
      },
      name,
      label: 'Add interviewer pool',
      labelInfoText:
        'This is a list of interviewer pools in Kula. You can choose one or add more by going to Settings -> Interviewer pools.',
      type: 'select',
      isRequired: true,
      isMulti: false,
      containerStyles: { width: '100%' },
      options: interviewPoolsOptions,
      placeholder: 'Select a pool',
      loading: isLoading,
      showAfterContent: true,
      AfterContentsComp: InterviewPoolList,
      OptionRightComp: InterviewPoolPopover,
      components: {
        Option: SelectWithRightCompOption,
      },

      ...overRides,
    };
  }, [name, interviewPoolsOptions, isLoading, overRides]);

  return formConfig;
};
